import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import isNil from "lodash/isNil";
import { useMemo } from "react";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import Txt from "../../typography/Txt";
import RffConditional from "./RffConditional";
import RffFieldComponentRenderer from "./RffFieldComponentRenderer";

const CELL_FONT_SIZE = 12; // Default font size for cell values
const SEPERATOR_COLOR = "#dde0e4"; // Default separator color

const RffDataRow = ({
  label,
  name,
  type,
  options,
  suppressLabel,
  cellValueSize = 6,
  disabled,
  stacked,
  suppressBottomBorder,
  placeholder,
  grow = 0,
  data,
  hidden = false,
  onUpdateFn,
  wrapWidth = "200px",
  condition,
  gridTemplateColumns,
  topBorder = false,
}) => {
  const rowProps = {
    type,
    name,
    options,
    label,
    suppressLabel,
    cellValueSize,
    disabled,
    placeholder,
    suppressBottomBorder,
    stacked,
    hidden,
    wrapWidth,
    onUpdateFn,
    grow,
    gridTemplateColumns,
    topBorder,
    data,
  };

  if (!condition) {
    return <RowContent {...rowProps} />;
  }

  return (
    <RffConditional condition={condition}>
      <RowContent {...rowProps} />
    </RffConditional>
  );
};

const RowContent = ({
  label,
  name,
  type,
  options,
  suppressLabel,
  cellValueSize,
  disabled,
  placeholder,
  wrapWidth,
  hidden,
  stacked,
  gridTemplateColumns,
  topBorder,
  loading,
}) => {
  const gridRowStyles = useMemo(() => {
    if (stacked) {
      return {};
    }
    return {
      display: hidden ? "none" : "grid",
      borderTop: topBorder ? `1px dashed ${SEPERATOR_COLOR}` : "none",
      borderBottom: `1px dashed ${SEPERATOR_COLOR}`,
      ".input-type--booleanCheckbox": {
        padding: "0 14px",
      },
      ".input-type--booleanToggle , .input-type--selectToggle": {
        padding: "8.5px 10px",
        ".MuiToggleButtonGroup-root": {
          mt: 0,
        },
      },
    };
  }, [stacked, topBorder, hidden]);

  return (
    <FlxGrid
      container
      width={wrapWidth}
      gridTemplateColumns={gridTemplateColumns}
      gap={0}
      className={`editable-data-row ${stacked ? " stacked" : ""}`}
      sx={gridRowStyles}
    >
      <RowLabelCell
        label={label}
        type={type}
        stacked={stacked}
        cellValueSize={cellValueSize}
        suppressLabel={suppressLabel}
      />
      <RowValueCell
        name={name}
        type={type}
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        fontSize={CELL_FONT_SIZE}
        cellValueSize={cellValueSize}
        stacked={stacked}
        loading={loading}
      />
    </FlxGrid>
  );
};

const RowLabelCell = ({
  label,
  suppressLabel,
  type,
  cellValueSize,
  stacked,
}) => {
  const labelCellStyles = useMemo(() => {
    let defaultStyles = {
      display: "flex",
      alignItems: "center",
      height: "100%",
    };
    return defaultStyles;
    return type === "stringMultiline" ? { mt: 0 } : { ...defaultStyles };
  }, [type]);

  if (suppressLabel || isNil(label)) return null;

  return (
    <Box
      className="editable-data-row--label"
      sx={{ p: 0.8, ...labelCellStyles }}
    >
      <Txt sx={{ color: "#33475b", fontSize: CELL_FONT_SIZE }}>{label}</Txt>
    </Box>
  );
};

const RowValueCell = ({
  name,
  type,
  options,
  disabled,
  placeholder,
  stacked,
  topBorder,
  color,
  fontSize,
}) => {
  const valueCellStyles = useMemo(() => {
    let defaultStyles = {
      position: "relative",
      display: "flex",
      flexGrow: 1,
      flexWrap: "wrap",
      gap: 0.5,
      // background: disabled ? "#e4e4e435" : "#e3f2fd35", // blue[50],
      background: disabled ? "#f4f4f4" : "#e3f2fd35", // blue[50],
      boxShadow: disabled ? "inset 0 0 0px 3px #FFFFFF" : "none",
      // background: disabled ? grey[50] : "#e3f2fd35", // blue[50],
      // background: "#e3f2fd4f", // blue[50],
      // borderColor: blue[50],
      border: `1px solid ${disabled ? grey[200] : "#e3f2fdff"}`,
      borderBottom: "none",
      borderTop: "none",
      // borderTop: topBorder ? "none" : "1px solid red",
      // borderTop: topBorder ? "none" : "1px solid #e3f2fdff",
      // borderRadius: "8px",
      justifyContent: "flex-end",
      fieldset: {
        borderColor: "transparent",
      },
      ".MuiGrid-item": {
        flexGrow: 1,
      },
      ".MuiInputBase-root, .MuiButtonBase-root": {
        fontWeight: 600,
        "&:not(.MuiCheckbox-root)": {
          color,
          fontSize,
          fontWeight: 600,
          "& input::placeholder": {
            fontWeight: "normal !important",
          },
          "& .MuiSelect-select[value='']": {
            fontWeight: 600,
          },
          "& .MuiSelect-select": {
            span: {
              fontWeight: "normal !important",
            },
          },

          "& input::placeholder, & textarea::placeholder": {
            fontWeight: "normal !important",
          },
        },
        "&.Mui-disabled": {
          fontWeight: "600 !important",
          // input: {
          //   "-webkit-text-fill-color": `${color} !important`,
          // },
          "& fieldset": {
            borderColor: "transparent",
          },
          "& input::placeholder, & textarea::placeholder": {
            fontWeight: 600,
          },
          "& .MuiSelect-select[value='']": {
            fontWeight: 600,
          },
        },
      },
      ".MuiToggleButton-root": {
        fontWeight: "normal !important",
        "&.Mui-selected": {
          fontWeight: "600 !important",
        },
      },
    };

    if (!stacked) {
      defaultStyles.alignItems = "center";
    }
    if (stacked) {
      defaultStyles.display = "inline-flex";
      defaultStyles.whiteSpace = "nowrap";
      defaultStyles.width = "fit-content";
      defaultStyles[".MuiFormControl-root"] = {
        width: "fit-content",
      };
      defaultStyles[".MuiOutlinedInput-root"] = {
        width: "auto",
        paddingRight: 0,
      };
      defaultStyles[".MuiOutlinedInput-input"] = {
        width: "auto",
        minWidth: 0,
        minHeight: "unset !important",
        pl: 0.8,
        lineHeight: 1,
      };
      defaultStyles[".MuiInputAdornment-root"] = {
        marginLeft: 0,
      };
    }

    return defaultStyles;
  }, [stacked, color, fontSize, disabled]);

  const fieldProps = useMemo(() => {
    return {
      type,
      field: name,
      options,
      disabled,
      placeholder,
    };
  }, [type, name, options, disabled, placeholder]);

  const content = <RffFieldComponentRenderer field={fieldProps} />;

  return (
    <Box
      className={`editable-data-row--value input-type--${type}`}
      sx={valueCellStyles}
    >
      {content}
    </Box>
  );
  // if (stacked) {
  //   return (
  //     <Box
  //       className={`editable-data-row--value input-type--${type}`}
  //       sx={gridRootStyles}
  //     >
  //       {content}
  //     </Box>
  //   );
  // }

  // return (
  //   <Grid2
  //     size={gridSize}
  //     className={`editable-data-row--value input-type--${type}`}
  //     sx={gridRootStyles}
  //   >
  //     {content}
  //   </Grid2>
  // );
};

export default RffDataRow;
